/**
 * Coaching client passcode login — isolated from trichology client login.
 * Route: /coaching/login
 * Stores auth in localStorage as `me_i_see_token`, then redirects to /coaching/me-i-see.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoachingClientLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [passcode, setPasscode] = useState("");
  const [step, setStep] = useState("email"); // email | verify
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devCode, setDevCode] = useState("");

  async function requestPasscode(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/auth/coaching-client/request-passcode`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const body = await r.json();
      if (body.passcode_dev) setDevCode(body.passcode_dev);
      setStep("verify");
    } catch { setError("Could not send passcode. Please try again."); }
    setLoading(false);
  }
  async function verify(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/auth/coaching-client/verify-passcode`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), passcode: passcode.trim() }),
      });
      if (!r.ok) { setError("Invalid or expired passcode."); setLoading(false); return; }
      const body = await r.json();
      localStorage.setItem("me_i_see_token", body.token);
      localStorage.setItem("me_i_see_email", body.email);
      if (body.name) localStorage.setItem("me_i_see_name", body.name);
      navigate("/coaching/me-i-see");
    } catch { setError("Could not verify passcode. Please try again."); }
    setLoading(false);
  }

  return (
    <div data-testid="coaching-client-login" className="min-h-screen flex items-center justify-center px-6"
         style={{ background: "#f6f4f0", fontFamily: "'Inter', sans-serif" }}>
      <div className="w-full max-w-md bg-white rounded-2xl border p-8" style={{ borderColor: "#eee" }}>
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#5B2C83", fontWeight: 600 }}>
          Christy Vonta Coaching · KCIS Coaching Door
        </div>
        <h1 className="mt-2 text-3xl" style={{ fontFamily: "'Playfair Display', serif", color: "#111" }}>
          Sign in to your program
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {step === "email" ? "Enter the email you enrolled with. We'll send you a 6-digit passcode." : "Enter the 6-digit passcode we just sent to " + email + "."}
        </p>

        {step === "email" ? (
          <form onSubmit={requestPasscode} className="mt-6 space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: "#ddd" }}
              data-testid="login-email-input"
            />
            <button
              type="submit"
              disabled={loading || !email}
              data-testid="login-request-btn"
              className="w-full rounded-full px-6 py-3 text-sm font-medium text-white disabled:opacity-40 inline-flex items-center justify-center gap-2"
              style={{ background: "#5B2C83" }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send my passcode <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            {devCode && (
              <div className="text-xs rounded-xl p-3" style={{ background: "#f1eaf6", color: "#5B2C83" }}>
                <strong>Dev mode</strong> · your passcode is <code>{devCode}</code>
              </div>
            )}
            <input
              inputMode="numeric"
              maxLength={6}
              required
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit passcode"
              className="w-full rounded-xl border px-4 py-3 text-lg text-center tracking-widest focus:outline-none focus:ring-2"
              style={{ borderColor: "#ddd" }}
              data-testid="login-passcode-input"
            />
            <button
              type="submit"
              disabled={loading || passcode.length !== 6}
              data-testid="login-verify-btn"
              className="w-full rounded-full px-6 py-3 text-sm font-medium text-white disabled:opacity-40 inline-flex items-center justify-center gap-2"
              style={{ background: "#5B2C83" }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify &amp; enter <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={() => { setStep("email"); setPasscode(""); setDevCode(""); }} className="text-xs text-slate-500 hover:text-slate-700" data-testid="login-change-email-btn">
              Use a different email
            </button>
          </form>
        )}

        {error && <div className="mt-4 text-sm text-red-700" data-testid="login-error">{error}</div>}

        <div className="mt-8 text-xs text-slate-500">
          Not enrolled yet? <Link to="/coaching/the-me-i-see" className="underline" style={{ color: "#5B2C83" }}>Learn about The Me I See™</Link>
        </div>
      </div>
    </div>
  );
}
